import type { PricingShowcaseRuntimePresentationModel } from '@/cms/components/blocks/pricing/pricing-1/types';
import { parsePricingFeatureText } from '@/cms/components/blocks/pricing/shared/pricing-view-utils';
import {
  formatCmsPricingCurrency,
  formatPricingCheckoutTemplate,
} from '@/cms/pricing-checkout/runtime-copy';
import type { SiteRuntimePricingPurchaseOptions } from '../types';
import {
  findSiteRuntimePricingBillingOption,
  requireSiteRuntimeMonthlyBillingOption,
  resolveSiteRuntimeMaximumAnnualDiscountLabel,
} from './site-runtime-pricing-billing-options';

export function buildSiteRuntimePricingShowcasePresentationModel(params: {
  locale: string;
  model: SiteRuntimePricingPurchaseOptions;
}): PricingShowcaseRuntimePresentationModel {
  const subscriptionOptions = params.model.plans.map((plan) => {
    const monthly = requireSiteRuntimeMonthlyBillingOption({
      options: plan.billingOptions,
      planKey: plan.planKey,
    });
    const annual = findSiteRuntimePricingBillingOption({
      billingCycle: 'year',
      options: plan.billingOptions,
    });

    return {
      id: plan.planKey,
      name: plan.planName,
      monthlyAmountCents: monthly.amountCents,
      monthlyPriceDisplay: monthly.priceDisplay,
      monthlyReferenceDisplay: monthly.priceDisplay,
      currency: monthly.currency,
      creditsAmount: plan.creditsAmount,
      creditsLabel: formatPricingCheckoutTemplate(
        params.model.showcase.creditsMonthlyTemplate,
        { credits: plan.creditsAmount.toLocaleString(params.locale) }
      ),
      sliderBadgeLabel: plan.planName,
      sliderLabel: plan.creditsAmount.toLocaleString(params.locale),
      features: plan.features.map(parsePricingFeatureText),
      ...(annual
        ? {
            yearlyAmountCents: annual.amountCents,
            yearlyPriceDisplay: annual.priceDisplay,
            yearlyMonthlyEquivalentDisplay: formatCmsPricingCurrency({
              amountCents: annual.amountCents / 12,
              currency: annual.currency,
              locale: params.locale,
            }),
          }
        : {}),
    };
  });
  const creditPackOptions = params.model.creditPackages.map(
    (creditPackage) => ({
      id: creditPackage.packageKey,
      amount: creditPackage.amount,
      label: formatPricingCheckoutTemplate(
        params.model.showcase.creditsOneTimeTemplate,
        { credits: creditPackage.amount.toLocaleString(params.locale) }
      ),
      sliderBadgeLabel: creditPackage.packageName,
      sliderLabel: creditPackage.amount.toLocaleString(params.locale),
      priceDisplay: creditPackage.priceDisplay,
      amountCents: creditPackage.amountCents,
      currency: creditPackage.currency,
      features: creditPackage.features.map(parsePricingFeatureText),
    })
  );
  if (!subscriptionOptions.length || !creditPackOptions.length) {
    throw new Error(
      'Published Pricing requires subscription plans and credit packages to build its showcase presentation.'
    );
  }

  return {
    modelDiscounts: params.model.modelDiscounts,
    labels: {
      subscriptionPeriodMonthly: params.model.uiCopy.monthlyLabel,
      subscriptionPeriodYearly: params.model.uiCopy.yearlyLabel,
      oneTime: params.model.uiCopy.oneTimeLabel,
      billedYearly: params.model.showcase.billedYearlyLabel,
    },
    billingLabels: {
      monthly: params.model.uiCopy.monthlyLabel,
      annual: params.model.uiCopy.yearlyLabel,
      discount: resolveSiteRuntimeMaximumAnnualDiscountLabel(params.model),
    },
    subscriptionCard: {
      badgeLabel: params.model.showcase.subscriptionCardBadgeLabel,
      title: params.model.showcase.subscriptionCardTitle,
      sliderStartLabel: subscriptionOptions[0]?.sliderLabel ?? '',
      sliderEndLabel:
        subscriptionOptions[subscriptionOptions.length - 1]?.sliderLabel ?? '',
      options: subscriptionOptions,
    },
    creditPackCard: {
      title: params.model.showcase.creditPackCardTitle,
      sliderStartLabel: creditPackOptions[0]?.sliderLabel ?? '',
      sliderEndLabel:
        creditPackOptions[creditPackOptions.length - 1]?.sliderLabel ?? '',
      features: creditPackOptions[0]?.features ?? [],
      options: creditPackOptions,
    },
    freePlan: params.model.showcase.freePlan,
  };
}
